// "Paste with template…" — turn a list of clipboard records into a batch of
// statements using a user-typed template containing the `<RECORD>` placeholder.
//
// Example:
//   template:   "UPDATE tr_jual_m SET Sisa=0 WHERE NoJual = <RECORD>;"
//   clipboard:  "26003001\n26003005"
//   options:    quote='single', skipBlanks=true, addNewline=true
//   output:
//     UPDATE tr_jual_m SET Sisa=0 WHERE NoJual = '26003001';
//     UPDATE tr_jual_m SET Sisa=0 WHERE NoJual = '26003005';
//
// `<RECORD>` may appear ZERO, ONE, or MANY times in the template — every
// occurrence is substituted with the SAME (this iteration's) record.
//
// The pure transforms (`expandTemplate`, `splitClipboard`, `quoteRecord`,
// `previewExpansion`) are unit-tested under node — keep this file's TOP-LEVEL
// imports dependency-free so the test can import it without esbuild. The
// storage helpers import `api` (which transitively imports nothing UI-bound),
// also fine for node.

import { api } from './api.js';

const PREF_KEY = 'editor.pasteTemplates';
const HISTORY_CAP = 10;
const RECORD_PLACEHOLDER = '<RECORD>';

// quoteRecord wraps a record with the chosen quote char, doubling any embedded
// quote so the output is a valid SQL string/identifier literal
// (e.g. O'Brien → 'O''Brien'). quote ∈ '' (none) | "'" | '"'.
export function quoteRecord(line, quote) {
  if (!quote) return line;
  return quote + String(line).split(quote).join(quote + quote) + quote;
}

// splitClipboard turns raw clipboard text into an array of records.
// CR/LF tolerant. When skipBlanks is true, whitespace-only lines drop out
// (Excel often leaves trailing blanks). Records are NOT trimmed — leading /
// trailing spaces in a single line are preserved verbatim (the user might want
// them in the SQL); we only drop entirely-empty / whitespace-only lines.
export function splitClipboard(text, skipBlanks) {
  const lines = String(text ?? '').split(/\r?\n/);
  return skipBlanks ? lines.filter((s) => s.trim().length > 0) : lines;
}

// expandTemplate produces the final SQL block from a template + clipboard.
//   template     — string, may contain <RECORD> anywhere, including multiple times
//   text         — raw clipboard text
//   opts.quote   — '' | "'" | '"'      (None / Single / Double; from the modal radio)
//   opts.skipBlanks — boolean          (drop blank clipboard lines)
//   opts.addNewline — boolean          (join with \n between records)
// Returns the joined string (no trailing newline).
export function expandTemplate(template, text, opts = {}) {
  const { quote = '', skipBlanks = true, addNewline = true } = opts;
  const tpl = String(template ?? '');
  const lines = splitClipboard(text, skipBlanks);
  if (lines.length === 0) return '';
  const out = lines.map((line) => {
    const value = quoteRecord(line, quote);
    // Replace EVERY <RECORD> occurrence with the same record. split/join is
    // safer than .replaceAll on older browsers and doesn't need regex escaping
    // (the placeholder is a fixed literal).
    return tpl.split(RECORD_PLACEHOLDER).join(value);
  });
  return out.join(addNewline ? '\n' : '');
}

// previewExpansion expands at most `maxRows` records so the modal can render a
// live preview without doing the work for a 5000-row clipboard. Returns
// { text, truncatedFrom } where truncatedFrom is the full row count when the
// preview was clipped, otherwise null.
export function previewExpansion(template, text, opts = {}, maxRows = 5) {
  const lines = splitClipboard(text, opts.skipBlanks ?? true);
  if (lines.length === 0) return { text: '', truncatedFrom: null };
  const clipped = lines.slice(0, maxRows);
  const preview = expandTemplate(template, clipped.join('\n'), { ...opts, skipBlanks: false });
  return {
    text: preview,
    truncatedFrom: lines.length > maxRows ? lines.length : null,
  };
}

// ── Persistence ──────────────────────────────────────────────────────────────
// Server-stored via /api/me/prefs (key: editor.pasteTemplates). Cached in a
// module-level signal so re-opening the modal doesn't re-fetch.

const DEFAULT_OPTIONS = { quote: '', skipBlanks: true, addNewline: true };

let _cache = null; // null until first load — plain var so node-side tests can import this module

function normaliseLoaded(raw) {
  let parsed;
  try { parsed = typeof raw === 'string' ? JSON.parse(raw) : raw; }
  catch { parsed = null; }
  const history = Array.isArray(parsed?.history)
    ? parsed.history
        .filter((h) => h && typeof h.template === 'string' && h.template.length > 0)
        .slice(0, HISTORY_CAP)
        .map((h) => ({ template: h.template, lastUsed: Number(h.lastUsed) || 0 }))
    : [];
  const options = { ...DEFAULT_OPTIONS, ...(parsed?.options || {}) };
  // Coerce quote into the only legal values.
  if (options.quote !== "'" && options.quote !== '"') options.quote = '';
  options.skipBlanks = !!options.skipBlanks;
  options.addNewline = !!options.addNewline;
  return { history, options };
}

// loadPasteTemplates fetches once + caches. Resolves to { history, options }.
export async function loadPasteTemplates() {
  if (_cache) return _cache;
  let raw = null;
  try {
    const r = await api.get(`/api/me/prefs/${encodeURIComponent(PREF_KEY)}`);
    raw = r?.value;
  } catch {
    raw = null;
  }
  _cache = normaliseLoaded(raw);
  return _cache;
}

// recordTemplateUse pushes a freshly-used template to the front of the history
// (de-duped on exact-string match), persists the new state, and updates the
// in-memory cache. `options` is the last-used radio/checkbox set so the modal
// re-opens with the same defaults.
export async function recordTemplateUse(template, options) {
  const cur = _cache || { history: [], options: { ...DEFAULT_OPTIONS } };
  const trimmed = String(template ?? '').trimEnd();
  if (!trimmed) return cur;
  const filtered = cur.history.filter((h) => h.template !== trimmed);
  const nextHistory = [{ template: trimmed, lastUsed: Date.now() }, ...filtered].slice(0, HISTORY_CAP);
  const nextOptions = { ...cur.options, ...options };
  _cache = { history: nextHistory, options: nextOptions };
  try {
    await api.put(`/api/me/prefs/${encodeURIComponent(PREF_KEY)}`, {
      value: JSON.stringify(_cache),
    });
  } catch {
    // Best-effort — UI already updated optimistically.
  }
  return _cache;
}

// Test-only: clear the in-memory cache. (Not used by the app.)
export function _resetPasteTemplateCache() { _cache = null; }
